import { Injectable, inject } from '@angular/core';
import {AuthService, GlobalErrorHandler, NavigationService} from '../services';
import {Store} from '@ngrx/store';
import {BehaviorSubject, combineLatest, forkJoin, fromEvent, Observable, of, Subject, throwError} from 'rxjs';
import {catchError, filter, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {JwtHelperService} from '@auth0/angular-jwt';
import {Authentication, AuthorizationTokens} from '../entities';
import {RoutesEnum} from '../enums';
import {globalActions, globalSelectors} from '../+states';
import { convertToFormData, isValidJwt, isJwtFormatValid } from '../functions';
import {getUserRights} from '../functions';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class AuthSandbox {
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store);
  private readonly globalErrorHandler = inject(GlobalErrorHandler);
  private readonly router = inject(NavigationService);

  public static GLOBAL_KEY = 'global';
  public static AUTH_TOKEN_KEY = 'authToken';
  private jwtToken: string = null;
  private savedRefreshToken: string = null;
  private destroy$ = new Subject();
  private helper = new JwtHelperService();
  private isRefreshingToken = false;
  public token$ = new BehaviorSubject<string>(null);
  private newToken = '';
  private _lastLogin: { url: string; userId: number; partnerId: number } = null;

  public init() {
    this.storeToken$.pipe(takeUntil(this.destroy$)).subscribe((jwtToken) => {
      if (!!jwtToken) {
        this.refreshing = false;
      }
      this.token$.next(jwtToken);
      this.jwtToken = jwtToken;
      this.savedRefreshToken = this.getLocalStorageRefreshToken();
      this.storeAuthTokenLocal();
    });
  }

  public storeAuthTokenLocal() {
    localStorage.setItem(AuthSandbox.AUTH_TOKEN_KEY, this.jwtToken);
  }

  public userStatusChange$: Observable<string> = fromEvent(window, 'storage').pipe(
    switchMap(() => {
      try {
        const parsedStorage = JSON.parse(localStorage.getItem(AuthSandbox.GLOBAL_KEY) || '{}');
        const storedJWTToken = localStorage.getItem(AuthSandbox.AUTH_TOKEN_KEY) || '';
        return forkJoin([of(storedJWTToken), of(parsedStorage?.refreshToken)]);
      } catch (e) {
        this.globalErrorHandler.reportError(e, `UserStatusChange json Parse Error! JSON: ${localStorage.getItem(AuthSandbox.GLOBAL_KEY) || '{}'}`);
        return forkJoin([of(null), of(null)]);
      }
    }),
    map(([authToken, refreshToken]) => {
      if (!!refreshToken) {
        this.store.dispatch(globalActions.setRefreshToken({ refreshToken }));
      }

      if (authToken !== this.jwtToken) {
        if (!authToken && !!this.jwtToken && !refreshToken) {
          this.logout(true);
          return 'sign_out';
        } else if (!!authToken && !this.jwtToken) {
          this.setToken(authToken, this.getLocalStorageRefreshToken());
          return 'sign_in';
        }
      }
      return null;
    }),
    filter((event) => !!event)
  );

  public verifyLastLogin(): boolean {
    if (!this._lastLogin || !this.jwtToken || !isValidJwt(this.jwtToken)) {
      return;
    }
    const decodedJwt = this.helper.decodeToken(this.jwtToken);
    const actualUserId = +decodedJwt?.user_id;
    if (this._lastLogin.userId === actualUserId) {
      this.router.navigateByUrl(this._lastLogin.url);
      this._lastLogin = null;
      return true;
    }

    this._lastLogin = null;
    return false;
  }

  private getLocalStorageRefreshToken = (): string => JSON.parse(localStorage.getItem(AuthSandbox.GLOBAL_KEY))?.refreshToken;

  public goToNewToken(): void {
    if (!!this.newToken) {
      const token = this.newToken;
      this.newToken = '';
      this.setToken(token, this.getLocalStorageRefreshToken());
    }
  }

  public setCurrentToken(): void {
    if (!!this.jwtToken) {
      this.newToken = '';
      this.setToken(this.jwtToken, this.savedRefreshToken || this.getLocalStorageRefreshToken());
    }
  }

  private setToken = (token: string, refreshToken: string = '') => {
    this.token$.next(token);
    this.jwtToken = token;
    this.savedRefreshToken = refreshToken || this.getLocalStorageRefreshToken();
    this.store.dispatch(refreshToken ? globalActions.setAuthorizationData({ token, refreshToken }) : globalActions.setToken({ token }));
  };

  public isAuthenticated = () => this.store.select(globalSelectors.isUserAuthenticated());

  public getJwtToken = () => this.jwtToken;

  public isTokenExpired(token: string) {
    const expired = token ? isValidJwt(token) : true;
    if (expired) {
      this.refreshToken();
    }
    return expired;
  }

  public storeToken$ = combineLatest([
    this.store.select(globalSelectors.getAuthorizationToken()),
    this.store.select(globalSelectors.getRefreshToken())
  ]).pipe(
    switchMap(([authToken, refreshToken]) => {
      if (!!authToken) {
        return of(authToken);
      } else if (!!refreshToken) {
        return this.authService.refreshToken(refreshToken, !!authToken ? authToken : localStorage.getItem(AuthSandbox.AUTH_TOKEN_KEY)).pipe(
          tap((data: AuthorizationTokens) => {
            this.setToken(data.authToken, data.refreshToken);
          }),
          map((value) => value.authToken),
          catchError((error: HttpErrorResponse) => {
            if (error.status === HttpStatusCode.Unauthorized) {
              this.logout(true);
            }
            return of(null);
          })
        );
      }
      return of(null);
    }),
    tap(() => {
      localStorage.removeItem(AuthSandbox.AUTH_TOKEN_KEY);
    })
  );

  public destroy = () => {
    this.destroy$.next(null);
    this.destroy$.complete();
  };

  public set refreshing(refreshing: boolean) {
    this.isRefreshingToken = refreshing;
    if (this.isRefreshingToken) {
      this.token$.next(null);
    }
  }

  public get refreshing() {
    return this.isRefreshingToken;
  }

  public login = (authentication: Authentication): Observable<boolean> => {
    const authData = {
      ...authentication,
      ...(!!this._lastLogin?.partnerId ? { partnerId: this._lastLogin.partnerId } : null)
    };

    return this.authService.login(convertToFormData(authData)).pipe(
      map((data) => {
        this.store.dispatch(
          globalActions.setAuthentication({
            authorization: {
              ...data,
              user: getUserRights(data.user)
            }
          })
        );
        this.jwtToken = data.authToken;
        return this.verifyLastLogin();
      })
    );
  };

  public logout(saveUrl: boolean = false, jwtToken: string = null, navigationUrl: string = null) {
    const tokenToCheck = jwtToken ?? this.jwtToken;
    if (saveUrl && tokenToCheck && isJwtFormatValid(tokenToCheck)) {
      const decodedJwt = this.helper.decodeToken(tokenToCheck);
      const url = navigationUrl ?? this.router.url;
      const userId = +decodedJwt.user_id;
      const partnerId = +decodedJwt.partner_id;

      if (url && userId) {
        this._lastLogin = { url, userId, partnerId };
      }
    } else {
      this._lastLogin = null;
    }
    this.jwtToken = null;
    this.savedRefreshToken = null;
    this.store.dispatch(globalActions.clearGlobalState());
    this.router.navigate([RoutesEnum.LOGIN]);
  }

  public readonly registration = (registration) => this.authService.registration(registration);

  public readonly activate = (token) => this.authService.activate(token);

  public readonly resetPassword = (email: string) => this.authService.resetPassword(email);

  public readonly setPassword = (password: string, token: string) => this.authService.setPassword(password, token);

  public refreshToken() {
    const jwtToken = this.jwtToken;
    const navigationUrl = this.router.url;
    this.refreshing = true;
    this.store
      .select(globalSelectors.getRefreshToken())
      .pipe(
        take(1),
        switchMap((refreshToken) =>
          refreshToken
            ? this.authService
                .refreshToken(refreshToken, jwtToken)
                .pipe(catchError(() => throwError({ error: 'LBL_INPUT.ERROR.EXPIRED_LOGIN', status: HttpStatusCode.Unauthorized })))
            : throwError({ error: 'LBL_INPUT.ERROR.EXPIRED_LOGIN', status: HttpStatusCode.Unauthorized })
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.error && err.status === HttpStatusCode.Unauthorized) {
            this.logout(true, jwtToken, navigationUrl);
          }
          return of(null);
        }),
        filter((value) => !!value)
      )
      .subscribe((authentication: AuthorizationTokens) => {
        this.setToken(authentication.authToken, authentication.refreshToken);
        this.token$.next(authentication.authToken);
        this.isRefreshingToken = false;
        return true;
      });
  }
}
